"use client";

import { useState } from "react";

import { ApiError } from "@/lib/api-types";

import {
  noticeForState,
  stateForScenario,
  type RequestCopy,
  type RequestScenario,
  type RequestState,
} from "@/features/auth/components/auth-request-content";

/**
 * The one behaviour model behind `/forgot` and `/signup/resend_activation`, so
 * their designs differ only in composition — never in what the page does.
 *
 * Mirrors the legacy flow 1:1 (POST the address, branch on the status): 200 →
 * success notice replaces the form, 404 → "we could not find your email" with
 * the form still there to correct, anything else → the generic failure.
 *
 * A `?scenario=` override pins the display state and disables submission, so a
 * reviewer can see every state without a backend.
 */
export interface EmailRequestPageModel {
  email: string;
  setEmail: (value: string) => void;
  state: RequestState;
  /** The notice to show, or null on the plain first visit. */
  notice: string | null;
  /** True while the request is in flight (or forced by `?scenario=loading`). */
  busy: boolean;
  /** True once the request succeeded — live replaces the form with a back-link. */
  done: boolean;
  onSubmit: (event: React.FormEvent) => void;
}

export function useEmailRequestPage({
  scenario,
  copy,
  submit,
}: {
  scenario: RequestScenario;
  copy: RequestCopy;
  /** The repository call this page posts to. Resolves on 2xx, throws otherwise. */
  submit: (email: string) => Promise<void>;
}): EmailRequestPageModel {
  const forced = scenario === "empty" ? null : stateForScenario(scenario);
  const [email, setEmail] = useState("");
  const [live, setLive] = useState<RequestState>("idle");

  const state = forced ?? live;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    // A pinned scenario is a display state, not a sandbox to submit from.
    if (forced || live === "sending" || !email.trim()) return;

    setLive("sending");
    submit(email.trim())
      .then(() => setLive("sent"))
      .catch((error: unknown) => {
        setLive(
          error instanceof ApiError && error.status === 404
            ? "notFound"
            : "failed",
        );
      });
  }

  return {
    email,
    setEmail,
    state,
    notice: noticeForState(copy, state),
    busy: state === "sending",
    done: state === "sent",
    onSubmit,
  };
}
